import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useT } from '../context/LocaleContext';
import BrandMark, { logoForBrand, useBrandLogos } from '../components/BrandMark';
import Sheet from '../components/Sheet';
import CustomerPanel from '../components/CustomerPanel';
import { supabase } from '../lib/supabase';
import { displayName, formatDate, vehicleTitle } from '../lib/format';

const PAGE_SIZE = 15;

function initialsFor(row) {
  const first = (row.first_name || '').trim().charAt(0);
  const last = (row.last_name || '').trim().charAt(0);
  const fromParts = `${first}${last}`.toUpperCase();
  if (fromParts) return fromParts;
  return displayName(row).charAt(0).toUpperCase() || '?';
}

export default function CustomersPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const openId = params.get('open');
  const pageParam = Math.max(1, Number(params.get('page')) || 1);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const makes = useBrandLogos();

  useEffect(() => {
    let alive = true;
    async function load() {
      const { data, error: queryError } = await supabase
        .from('profiles')
        .select(
          'id, first_name, last_name, full_name, phone, email, created_at, vehicles(id, brand, model, year, plate_number)',
        )
        .eq('role', 'customer')
        .order('created_at', { ascending: false });
      if (!alive) return;
      setLoading(false);
      if (queryError) {
        setError(queryError.message);
        return;
      }
      setRows(data || []);
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => {
      const hay = [
        displayName(row),
        row.phone,
        row.email,
        ...(row.vehicles || []).map(vehicleTitle),
        ...(row.vehicles || []).map((v) => v.plate_number),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(pageParam, pageCount);
  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const rangeStart = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, filtered.length);

  function setPage(nextPage) {
    const safe = Math.min(Math.max(1, nextPage), pageCount);
    const next = new URLSearchParams(params);
    if (safe <= 1) next.delete('page');
    else next.set('page', String(safe));
    setParams(next, { replace: true });
  }

  function onSearchChange(value) {
    setQuery(value);
    if (params.get('page')) {
      const next = new URLSearchParams(params);
      next.delete('page');
      setParams(next, { replace: true });
    }
  }

  function openCustomer(id) {
    const next = new URLSearchParams(params);
    next.set('open', id);
    setParams(next, { replace: true });
  }

  function closeCustomer() {
    const next = new URLSearchParams(params);
    next.delete('open');
    setParams(next, { replace: true });
  }

  const openRow = rows.find((row) => row.id === openId);

  return (
    <section className="customers-page">
      <header className="page-head customers-head">
        <div>
          <span className="kicker">{t('customers.kicker')}</span>
          <h1>{t('customers.title')}</h1>
          <p className="lede">{t('customers.lede')}</p>
        </div>
        <label className="customers-search">
          <span className="material-symbol" aria-hidden="true">
            search
          </span>
          <input
            className="search"
            placeholder={t('customers.search')}
            value={query}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </label>
      </header>

      {error && <p className="form-error">{error}</p>}

      {loading ? (
        <p className="empty">{t('customers.loading')}</p>
      ) : filtered.length === 0 ? (
        <p className="empty">{t('customers.none')}</p>
      ) : (
        <>
          <div className="customers-toolbar">
            <p className="customers-count">
              {t('customers.showing', {
                from: rangeStart,
                to: rangeEnd,
                total: filtered.length,
              })}
            </p>
            {query.trim() ? (
              <button
                type="button"
                className="text-link"
                onClick={() => onSearchChange('')}
              >
                {t('customers.clearSearch')}
              </button>
            ) : null}
          </div>

          <div className="customers-directory">
            <div className="customers-table-head" aria-hidden="true">
              <span>{t('customers.colCustomer')}</span>
              <span>{t('customers.colContact')}</span>
              <span>{t('customers.colGarage')}</span>
              <span>{t('customers.colJoined')}</span>
              <span />
            </div>
            {pageRows.map((row) => {
              const cars = row.vehicles || [];
              const first = cars[0];
              const name = displayName(row);
              const garageLabel = first
                ? [
                    first.plate_number || null,
                    vehicleTitle(first),
                    cars.length > 1 ? `+${cars.length - 1}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : t('customers.noCarsListed');
              return (
                <button
                  key={row.id}
                  type="button"
                  className="customer-row"
                  onClick={() => openCustomer(row.id)}
                >
                  <div className="customer-row-person">
                    <span className="customer-row-avatar" aria-hidden="true">
                      {initialsFor(row)}
                    </span>
                    <span className="customer-row-name">{name}</span>
                  </div>
                  <div className="customer-row-contact">
                    <span>{row.phone || t('customer.noPhone')}</span>
                    <span>{row.email || '—'}</span>
                  </div>
                  <div className="customer-row-garage">
                    {first ? (
                      <BrandMark
                        name={first.brand}
                        src={logoForBrand(makes, first.brand)}
                        compact
                      />
                    ) : null}
                    <span>{garageLabel}</span>
                  </div>
                  <div className="customer-row-joined">
                    {row.created_at ? formatDate(row.created_at) : '—'}
                  </div>
                  <span className="material-symbol customer-row-chevron">
                    chevron_right
                  </span>
                </button>
              );
            })}
          </div>

          {pageCount > 1 ? (
            <nav className="customers-pager" aria-label={t('customers.pagination')}>
              <button
                type="button"
                className="customers-pager-btn"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                <span className="material-symbol">chevron_left</span>
                {t('customers.prev')}
              </button>
              <p className="customers-pager-status">
                {t('customers.pageOf', { page, pages: pageCount })}
              </p>
              <button
                type="button"
                className="customers-pager-btn"
                disabled={page >= pageCount}
                onClick={() => setPage(page + 1)}
              >
                {t('customers.next')}
                <span className="material-symbol">chevron_right</span>
              </button>
            </nav>
          ) : null}
        </>
      )}

      {openId && (
        <Sheet
          variant="modal"
          wide
          className="sheet-customer"
          bodyClassName="sheet-body-customer"
          title={openRow ? displayName(openRow) : 'Customer'}
          subtitle={t('customers.sheetSubtitle')}
          onClose={closeCustomer}
        >
          <CustomerPanel id={openId} compact />
        </Sheet>
      )}
    </section>
  );
}
